/**
 * One edit: the diff, its sources and note, the review decision, votes and discussion.
 * Reviewers approve, correct and apply, or reject (with a reason); others vote; the author can
 * withdraw. An edit a reviewer corrected also shows what they changed.
 */
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { Check, ExternalLink, Pencil, ThumbsDown, ThumbsUp, TriangleAlert, X } from 'lucide-react'
import { Avatar, ErrorState, Loading, StatusBadge, Time } from '../components/Bits'
import { Diff } from '../components/Diff'
import { ReportButton } from '../components/RecordActions'
import { api, ApiError } from '../lib/api'
import { recordHref, TYPE_LABELS } from '../lib/format'
import { invalidate, useQuery } from '../lib/query'
import { signInHref, useSession } from '../lib/session'
import { useTitle } from '../lib/useTitle'
import type { EditDetail } from '../lib/types'
import { changedFields, ENTITY_PATHS, rebaseEdit, type EntityType } from '../../shared/model'

const ACTION_TITLE = { create: 'New', update: 'Edit to', delete: 'Deletion of', merge: 'Merge of' } as const

export function EditPage() {
  const { id = '' } = useParams()
  const session = useSession()
  const navigate = useNavigate()
  const { data: e, error, refresh } = useQuery<EditDetail>(`/api/v1/edits/${id}`, { staleMs: 5_000 })
  const [reason, setReason] = useState('')
  const [comment, setComment] = useState('')
  const [busy, setBusy] = useState('')
  const [err, setErr] = useState('')
  useTitle(e ? `${ACTION_TITLE[e.action]} ${e.title}` : 'Edit')

  if (error) return <ErrorState error={error} retry={refresh} />
  if (!e) return <Loading label="Loading edit" />
  const type = e.entity_type as EntityType

  const act = async (what: string, path: string, json: unknown) => {
    setBusy(what)
    setErr('')
    try {
      const res = await api<{ id: string }>(`/api/v1/edits/${id}/${path}`, { method: 'POST', json })
      invalidate(`/api/v1/edits`, `/api/v1/stats`, `/api/v1/${type}s/${e.entity_id}`, '/api/v1/me')
      // Submitting files the draft as a new edit and deletes the draft: follow it to its new page.
      if (what === 'submit') return navigate(`/edits/${res.id}`, { replace: true })
      await refresh()
      if (what === 'comment') setComment('')
      if (what === 'reject') setReason('')
    } catch (x) {
      setErr(x instanceof ApiError ? x.message : 'Something went wrong')
    } finally {
      setBusy('')
    }
  }

  const voted = e.my_vote
  const canVote = session.user && e.status === 'pending' && e.author_id !== session.user.id && !e.can_review
  const recordLink = e.status === 'applied' || e.action !== 'create' ? recordHref(type, e.entity_id) : null
  const correctable = e.action === 'create' || e.action === 'update'
  const formHref = (param: string) =>
    e.action === 'create'
      ? `/${ENTITY_PATHS[type]}/new?${param}=${e.id}`
      : `/${ENTITY_PATHS[type]}/${e.entity_id}/edit?${param}=${e.id}`
  // What the author submitted, on the same base as what was applied, so the corrections diff shows
  // only the reviewer's changes.
  const submitted = e.submitted
  const authors = submitted
    ? e.action === 'create'
      ? (submitted.after as Record<string, unknown>)
      : rebaseEdit(e.before as Record<string, unknown>, submitted.after as object, submitted.fields)
    : null

  return (
    <div className="page edit-page">
      <nav aria-label="Breadcrumb" className="crumbs">
        <ol>
          <li>
            <Link to={e.status === 'pending' ? '/review' : '/changes'}>
              {e.status === 'pending' ? 'Review queue' : 'Recent changes'}
            </Link>
          </li>
        </ol>
      </nav>
      <header className="edit-head">
        <h1 className="page-title">
          {ACTION_TITLE[e.action]} {TYPE_LABELS[type].one.toLowerCase()}{' '}
          {recordLink ? <Link to={recordLink}>{e.title}</Link> : e.title}
        </h1>
        <div className="edit-meta">
          <StatusBadge status={e.status} auto={e.auto_applied} />
          {e.author && (
            <Link to={`/users/${e.author.id}`} className="person">
              <Avatar name={e.author.name} src={e.author.avatar_url} size={20} /> {e.author.name}
            </Link>
          )}
          <span>
            submitted <Time ms={e.created_at} />
          </span>
          {e.closed_at && e.status !== 'pending' && (
            <span>
              {e.status} <Time ms={e.closed_at} />
              {e.reviewer
                ? ` by ${e.reviewer.name}${submitted ? ', with corrections' : ''}`
                : e.auto_applied && e.status === 'applied'
                  ? e.review_note
                    ? ` (${e.review_note})`
                    : ' directly'
                  : ''}
            </span>
          )}
        </div>
      </header>

      {e.status === 'rejected' && e.review_note && (
        <div className="notice notice-danger">
          <strong>Why it was rejected:</strong> {e.review_note}
        </div>
      )}
      {e.conflicts.length > 0 && (
        <div className="notice notice-warning" role="alert">
          <TriangleAlert size={18} aria-hidden="true" />
          <span>
            The record changed after this edit was made, so it can no longer be applied as is. The highlighted fields
            were changed to something else since.{' '}
            {e.can_review && correctable
              ? 'Correct it against the current version to settle them, then apply it.'
              : e.can_withdraw
                ? 'Withdraw it and edit the current version instead.'
                : 'It needs to be resubmitted.'}
          </span>
        </div>
      )}

      <div className="edit-layout">
        <div className="edit-main">
          {(e.note || e.sources.length > 0) && (
            <section className="edit-why">
              {e.note && <p className="edit-note">{e.note}</p>}
              {e.sources.length > 0 && (
                <ul className="link-list">
                  {e.sources.map((s) => (
                    <li key={s}>
                      <a href={s} target="_blank" rel="noopener noreferrer nofollow ugc">
                        <span>{new URL(s).hostname.replace(/^www\./, '')}</span>
                        <ExternalLink size={14} aria-hidden="true" />
                      </a>
                      <span className="link-host">{s}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
          {e.status === 'pending' && e.review_note && e.conflicts.length === 0 && (
            <p className="notice notice-pending">Waiting for review: {e.review_note}.</p>
          )}
          {e.action === 'merge' ? (
            <section className="merge-summary" aria-label="Merge">
              <h2 className="section-title">Merge</h2>
              <p>
                <strong>{e.title}</strong> will be folded into{' '}
                <Link to={recordHref(type, (e.after as { merge_into: string }).merge_into)}>
                  {(e.after as { merge_into_name?: string }).merge_into_name ?? 'the other record'}
                </Link>
                . Everything that points at it will point there instead; its name becomes an alias, and empty fields
                there are filled from it. Its history is kept.
              </p>
            </section>
          ) : e.action === 'delete' ? (
            <p className="notice notice-danger">
              This edit deletes the record. Its history is kept and it can be restored by a moderator.
            </p>
          ) : (
            <section aria-label="Changes">
              <h2 className="section-title">{e.action === 'create' ? 'Details' : 'Changes'}</h2>
              <Diff
                type={type}
                before={e.before as Record<string, unknown> | null}
                after={e.after as Record<string, unknown> | null}
                fields={e.fields}
                conflicts={e.conflicts}
              />
            </section>
          )}
          {authors && (
            <section aria-label="Corrections">
              <h2 className="section-title">Corrected by {e.reviewer?.name ?? 'the reviewer'}</h2>
              <p className="section-note">
                What {e.author?.name ?? 'the author'} submitted, and what the reviewer changed before applying it.
              </p>
              <Diff
                type={type}
                before={authors}
                after={e.after as Record<string, unknown>}
                fields={changedFields(authors, e.after as object)}
                conflicts={[]}
              />
            </section>
          )}

          <section className="comments" aria-labelledby="discussion">
            <h2 className="section-title" id="discussion">
              Discussion
            </h2>
            {e.comments.length === 0 && <p className="section-note">No comments yet.</p>}
            <ol className="comment-list">
              {e.comments.map((c) => (
                <li key={c.id} className="comment">
                  <Avatar name={c.user.name} src={c.user.avatar_url} size={28} />
                  <div>
                    <p className="comment-meta">
                      <Link to={`/users/${c.user.id}`}>{c.user.name}</Link>
                      {c.user.role !== 'user' && <span className="role-tag">{c.user.role}</span>}
                      <Time ms={c.created_at} />
                    </p>
                    <p className="comment-body">{c.body}</p>
                  </div>
                </li>
              ))}
            </ol>
            {session.user ? (
              <form
                className="comment-form"
                onSubmit={(ev) => {
                  ev.preventDefault()
                  void act('comment', 'comments', { body: comment })
                }}
              >
                <label htmlFor="comment" className="sr-only">
                  Add a comment
                </label>
                <textarea
                  id="comment"
                  rows={3}
                  value={comment}
                  onChange={(ev) => setComment(ev.target.value)}
                  placeholder="Ask for a source, suggest a fix, or explain a choice…"
                  maxLength={4000}
                />
                <button type="submit" className="button button-quiet" disabled={!comment.trim() || busy !== ''}>
                  {busy === 'comment' ? 'Posting…' : 'Comment'}
                </button>
              </form>
            ) : (
              <p className="section-note">
                <Link to={signInHref(`/edits/${id}`)}>Sign in</Link> to join the discussion.
              </p>
            )}
          </section>
        </div>

        <aside className="edit-side" aria-label="Decision">
          {err && (
            <p className="notice notice-danger" role="alert">
              {err}
            </p>
          )}
          <div className="panel">
            <h2 className="panel-title">Votes</h2>
            <p className="votes">
              <span>
                <ThumbsUp size={16} aria-hidden="true" /> {e.votes.yes} approve
              </span>
              <span>
                <ThumbsDown size={16} aria-hidden="true" /> {e.votes.no} reject
              </span>
            </p>
            {canVote && (
              <div className="vote-buttons">
                <button
                  type="button"
                  className="button button-quiet"
                  aria-pressed={voted === 1}
                  disabled={busy !== ''}
                  onClick={() => act('vote', 'vote', { vote: voted === 1 ? 0 : 1 })}
                >
                  <ThumbsUp size={16} aria-hidden="true" /> {voted === 1 ? 'You approve' : 'Approve'}
                </button>
                <button
                  type="button"
                  className="button button-quiet"
                  aria-pressed={voted === -1}
                  disabled={busy !== ''}
                  onClick={() => act('vote', 'vote', { vote: voted === -1 ? 0 : -1 })}
                >
                  <ThumbsDown size={16} aria-hidden="true" /> {voted === -1 ? 'You object' : 'Object'}
                </button>
              </div>
            )}
            {e.status === 'pending' && !session.user && (
              <p className="section-note">
                <Link to={signInHref(`/edits/${id}`)}>Sign in</Link> to vote.
              </p>
            )}
          </div>

          {e.can_review && (
            <div className="panel panel-review">
              <h2 className="panel-title">Your review</h2>
              <button
                type="button"
                className="button button-primary button-block"
                disabled={busy !== '' || e.conflicts.length > 0}
                onClick={() => act('approve', 'approve', { note: '' })}
              >
                <Check size={16} aria-hidden="true" /> {busy === 'approve' ? 'Applying…' : 'Approve and apply'}
              </button>
              {correctable && (
                <Link to={formHref('review')} className="button button-quiet button-block">
                  <Pencil size={16} aria-hidden="true" /> Correct, then apply
                </Link>
              )}
              <form
                className="reject-form"
                onSubmit={(ev) => {
                  ev.preventDefault()
                  void act('reject', 'reject', { note: reason })
                }}
              >
                <label htmlFor="reason">Or reject, saying why</label>
                <textarea
                  id="reason"
                  rows={3}
                  value={reason}
                  onChange={(ev) => setReason(ev.target.value)}
                  placeholder="For example: the year is the PDF release, the print edition came out in 2019."
                />
                <button
                  type="submit"
                  className="button button-danger button-block"
                  disabled={!reason.trim() || busy !== ''}
                >
                  <X size={16} aria-hidden="true" /> {busy === 'reject' ? 'Rejecting…' : 'Reject'}
                </button>
              </form>
            </div>
          )}

          {e.status === 'draft' && e.can_withdraw && (
            <div className="panel panel-review">
              <h2 className="panel-title">Your draft</h2>
              <p className="section-note">Only you can see it. Submit it when it is ready.</p>
              <Link to={formHref('draft')} className="button button-quiet button-block">
                Keep editing
              </Link>
              <button
                type="button"
                className="button button-primary button-block"
                disabled={busy !== ''}
                onClick={() => act('submit', 'submit', {})}
              >
                {busy === 'submit' ? 'Submitting…' : 'Submit'}
              </button>
              <button
                type="button"
                className="button button-danger-quiet button-block"
                disabled={busy !== ''}
                onClick={async () => {
                  if (!window.confirm('Discard this draft?')) return
                  await act('withdraw', 'withdraw', {})
                  navigate('/review?status=draft')
                }}
              >
                Discard draft
              </button>
            </div>
          )}

          {e.status === 'pending' && e.can_withdraw && (
            <div className="panel">
              <h2 className="panel-title">Your edit</h2>
              <p className="section-note">Withdraw it if you made a mistake. You can always submit a new one.</p>
              <button
                type="button"
                className="button button-quiet button-block"
                disabled={busy !== ''}
                onClick={() => act('withdraw', 'withdraw', {})}
              >
                {busy === 'withdraw' ? 'Withdrawing…' : 'Withdraw edit'}
              </button>
            </div>
          )}

          {e.status !== 'draft' && <ReportButton targetType="edit" targetId={e.id} />}
          {e.current_revision !== null && e.status === 'pending' && e.action !== 'create' && (
            <p className="section-note">
              Based on revision {e.base_revision}; the record is at revision {e.current_revision}.
            </p>
          )}
        </aside>
      </div>
    </div>
  )
}
